/**
 * WebAudio engine for Zoolympics. Ships rich *procedural* sound (no assets required):
 * referee whistle, crowd cheer, footsteps, impacts, a medal chime and the rhythmic beat.
 *
 * It also supports dropping in real recorded SFX (e.g. ElevenLabs mp3s): call
 * `loadSample(name, url)` and, if a sample is present, the matching high-level method
 * plays it instead of the synth — so call sites never change.
 *
 * WebAudio needs a user gesture to start, so `resume()` is called on first tap.
 */
class AudioEngineImpl {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private readonly samples = new Map<string, AudioBuffer>();
  private _muted = false;

  private ensure(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.55;
      this.master.connect(this.ctx.destination);
    }
    return this.ctx;
  }

  resume(): void {
    const ctx = this.ensure();
    if (ctx && ctx.state === 'suspended') void ctx.resume();
  }

  setMuted(muted: boolean): void {
    this._muted = muted;
    if (this.master) this.master.gain.value = muted ? 0 : 0.55;
  }

  get muted(): boolean {
    return this._muted;
  }

  // --- Recorded-sample support (ElevenLabs etc.) ---------------------------------

  /** Fetch + decode an audio file for later playback. Best-effort, never throws. */
  async loadSample(name: string, url: string): Promise<void> {
    try {
      const ctx = this.ensure();
      if (!ctx) return;
      const res = await fetch(url);
      if (!res.ok) return; // asset not present yet — keep the procedural fallback
      const arr = await res.arrayBuffer();
      const buf = await ctx.decodeAudioData(arr);
      this.samples.set(name, buf);
    } catch {
      /* missing/unsupported — the procedural fallback covers it */
    }
  }

  private playSample(name: string, gain = 1, rate = 1): boolean {
    const ctx = this.ensure();
    const buf = this.samples.get(name);
    if (!ctx || !this.master || !buf || this._muted) return false;
    const src = ctx.createBufferSource();
    const g = ctx.createGain();
    src.buffer = buf;
    src.playbackRate.value = rate;
    g.gain.value = gain;
    src.connect(g); g.connect(this.master);
    src.start();
    return true;
  }

  // --- Synth primitives ----------------------------------------------------------

  private blip(freq: number, durMs: number, type: OscillatorType, gain: number, glideTo?: number): void {
    const ctx = this.ensure();
    if (!ctx || !this.master || this._muted) return;
    const t0 = ctx.currentTime;
    const dur = durMs / 1000;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, t0 + dur);
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.exponentialRampToValueAtTime(gain, t0 + 0.005);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(env); env.connect(this.master);
    osc.start(t0); osc.stop(t0 + dur + 0.02);
  }

  private noise(): AudioBuffer | null {
    const ctx = this.ensure();
    if (!ctx) return null;
    if (!this.noiseBuf) {
      const len = ctx.sampleRate;
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      this.noiseBuf = buf;
    }
    return this.noiseBuf;
  }

  /** Filtered noise burst — the building block for footsteps, dust, impacts, crowd. */
  private noiseBurst(durMs: number, filterType: BiquadFilterType, freq: number, gain: number, attackMs = 5): void {
    const ctx = this.ensure();
    const buf = this.noise();
    if (!ctx || !this.master || !buf || this._muted) return;
    const t0 = ctx.currentTime;
    const dur = durMs / 1000;
    const src = ctx.createBufferSource();
    src.buffer = buf; src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = filterType; filter.frequency.value = freq; filter.Q.value = 0.8;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.linearRampToValueAtTime(gain, t0 + attackMs / 1000);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(filter); filter.connect(env); env.connect(this.master);
    src.start(t0); src.stop(t0 + dur + 0.02);
  }

  // --- High-level SFX (sample if present, else synth) ----------------------------

  /** Footstep: a soft filtered click + a tiny low thump. Pitch rises with intensity. */
  tap(intensity = 0): void {
    if (this.playSample('tap', 0.6, 0.95 + intensity * 0.2)) return;
    this.noiseBurst(55, 'lowpass', 900 + intensity * 700, 0.18 + intensity * 0.12, 2);
    this.blip(120 + intensity * 50, 50, 'sine', 0.12);
  }

  /** Steady metronome beat that paces the player's target cadence. */
  beat(): void {
    if (this.playSample('beat', 0.5)) return;
    this.blip(660, 40, 'square', 0.12);
  }

  countdownTick(): void {
    if (this.playSample('countdown', 0.8)) return;
    this.blip(440, 130, 'sine', 0.32);
  }

  /** Referee whistle for the start. */
  go(): void {
    if (this.playSample('whistle', 0.9)) return;
    // Two quick trilled chirps with a fast vibrato feel.
    this.blip(2100, 150, 'triangle', 0.26, 2400);
    setTimeout(() => this.blip(2300, 220, 'triangle', 0.26, 2000), 120);
    this.noiseBurst(120, 'bandpass', 2600, 0.05, 4);
  }

  /** Whoosh for a throw / jump take-off. */
  whoosh(): void {
    if (this.playSample('whoosh', 0.8)) return;
    this.noiseBurst(260, 'bandpass', 1100, 0.16, 30);
  }

  /** Heavy landing / impact thud (sand, mat, shot landing). */
  thud(): void {
    if (this.playSample('thud', 0.9)) return;
    this.blip(90, 180, 'sine', 0.3, 55);
    this.noiseBurst(160, 'lowpass', 500, 0.18, 3);
  }

  /** Bright bell — a cleared bar, a personal best, a medal. */
  chime(): void {
    if (this.playSample('chime', 0.8)) return;
    [880, 1320, 1760].forEach((f, i) => setTimeout(() => this.blip(f, 320, 'sine', 0.22), i * 70));
  }

  /** Crowd cheer/applause swell — filtered noise with a soft rise and fall. */
  crowd(durMs = 1100, gain = 0.22): void {
    if (this.playSample('crowd', gain * 3)) return;
    this.noiseBurst(durMs, 'bandpass', 1400, gain, durMs * 0.4);
    this.noiseBurst(durMs * 0.9, 'highpass', 3000, gain * 0.4, durMs * 0.35);
  }

  /** Finish fanfare: ascending arpeggio over a crowd cheer. */
  fanfare(): void {
    if (this.playSample('fanfare', 0.9)) { this.crowd(900, 0.18); return; }
    const notes = [523, 659, 784, 1047, 1319];
    notes.forEach((f, i) => setTimeout(() => this.blip(f, 240, 'triangle', 0.28), i * 95));
    this.crowd(1000, 0.2);
  }
}

export const AudioEngine = new AudioEngineImpl();
